import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import './Pagination.css';

const PageLink = memo(({ children, url, disabled, active, pageNum, onHover, ariaLabel, className = '' }) => {
    const handleMouseEnter = useCallback(() => {
        if (onHover && pageNum) {
            onHover(pageNum);
        }
    }, [onHover, pageNum]);

    const classes = [
        'al-pagination-item',
        disabled ? 'al-pagination-item--disabled' : '',
        active ? 'al-pagination-item--active' : '',
        className
    ].filter(Boolean).join(' ');

    if (!url || disabled) {
        return (
            <span
                className={classes}
                aria-disabled={disabled ? 'true' : undefined}
                aria-label={ariaLabel}
                aria-current={active ? 'page' : undefined}
            >
                {children}
            </span>
        );
    }

    return (
        <Link
            to={url}
            className={classes}
            onMouseEnter={handleMouseEnter}
            aria-label={ariaLabel}
            aria-current={active ? 'page' : undefined}
        >
            {children}
        </Link>
    );
});
PageLink.displayName = 'PageLink';

const Pagination = memo(({ getUrl, currentPage, hideNexts, totalPages, onPrefetch, loading }) => {
    const page = parseInt(currentPage, 10) || 1;
    const lastPage = totalPages || (hideNexts ? page : null);

    // Calculate which page numbers to show
    const getPageNumbers = () => {
        const pages = [];
        const showPages = 5; // Show up to 5 page numbers

        if (!lastPage || lastPage <= showPages) {
            // Show all pages if few enough
            for (let i = 1; i <= (lastPage || page + 2); i++) {
                pages.push(i);
            }
        } else {
            // Smart pagination with ellipsis
            if (page <= 3) {
                // Near start
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(lastPage);
            } else if (page >= lastPage - 2) {
                // Near end
                pages.push(1);
                pages.push('...');
                for (let i = lastPage - 3; i <= lastPage; i++) pages.push(i);
            } else {
                // Middle
                pages.push(1);
                pages.push('...');
                pages.push(page - 1);
                pages.push(page);
                pages.push(page + 1);
                pages.push('...');
                pages.push(lastPage);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();
    const canGoPrev = page > 1;
    const canGoNext = !hideNexts && (!lastPage || page < lastPage);

    return (
        <nav
            role="navigation"
            aria-label="Pagination"
            className={['al-pagination-nav', loading ? 'al-pagination-nav--loading' : ''].filter(Boolean).join(' ')}
        >
            <div className="al-pagination">
                {/* Previous button */}
                <PageLink
                    url={canGoPrev ? getUrl(page - 1) : undefined}
                    disabled={!canGoPrev}
                    pageNum={canGoPrev ? page - 1 : null}
                    onHover={onPrefetch}
                    ariaLabel="Previous page"
                    className="al-pagination-item--nav"
                >
                    <ChevronLeft size={20} />
                </PageLink>

                {/* Page numbers */}
                <div className="al-pagination-pages">
                    {pageNumbers.map((p, idx) => {
                        if (p === '...') {
                            return (
                                <span key={'ellipsis-' + idx} className="al-pagination-ellipsis">
                                    ...
                                </span>
                            );
                        }
                        return (
                            <PageLink
                                key={p}
                                url={p !== page ? getUrl(p) : undefined}
                                active={p === page}
                                pageNum={p}
                                onHover={onPrefetch}
                                ariaLabel={'Page ' + p}
                            >
                                {p}
                            </PageLink>
                        );
                    })}
                </div>

                {/* Next button */}
                <PageLink
                    url={canGoNext ? getUrl(page + 1) : undefined}
                    disabled={!canGoNext}
                    pageNum={canGoNext ? page + 1 : null}
                    onHover={onPrefetch}
                    ariaLabel="Next page"
                    className="al-pagination-item--nav"
                >
                    <ChevronRight size={20} />
                </PageLink>
            </div>

            {/* Page info */}
            {lastPage && (
                <div className="al-pagination-info">
                    Page {page} of {lastPage}
                </div>
            )}
        </nav>
    );
});

Pagination.propTypes = {
    getUrl: PropTypes.func.isRequired,
    currentPage: PropTypes.string,
    hideNexts: PropTypes.bool,
    totalPages: PropTypes.number,
    onPrefetch: PropTypes.func,
    loading: PropTypes.bool
};

Pagination.displayName = 'Pagination';

export default Pagination;
